import React, {FC} from 'react';
import styled from 'styled-components';
import Card from '../../Card/Card';
import Icon, {IconSize} from '../../Icon/Icon';
import {HelpCenterIconName} from '../../Icon/IconName';
import ClientSideLink from '../../Navigation/ClientSideLink/ClientSideLink';
import {
  bordersColor,
  complementaryColor,
  primaryColor,
} from '../../constants/colors';
import {
  mediaMinTablet,
  minTabletOnlyCss,
  mobileOnlyCss,
} from '../../constants/customMedia';
import {fsL, fwHeavy} from '../../constants/typography';
import HelpSectionTileQuestionLink from './HelpSectionTileQuestionLink';

const HelpSectionLinkMobile = styled(ClientSideLink)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 133px;
  ${mobileOnlyCss}
  padding: 20px;
  box-sizing: border-box;
  text-decoration: none;
  color: ${primaryColor};
`;
const HelpSectionLinkButton = styled(ClientSideLink)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  border-radius: 2px;
  border: solid 2px ${bordersColor};
  background-color: ${complementaryColor};
  color: ${primaryColor};
  text-decoration: none;
  &:hover {
    border-color: ${primaryColor};
  }
`;

const StyledCard = styled(Card)`
  padding: 0;
  display: flex;
  position: relative;
  @media ${mediaMinTablet} {
    padding: 40px;
  }
`;
const TileHeaderLink = styled(ClientSideLink)`
  text-decoration: none;
`;
const HelpSectionTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.29;
  color: ${primaryColor};

  @media ${mediaMinTablet} {
    ${fwHeavy};
    font-size: ${fsL}px;
    line-height: 1.17;
    letter-spacing: 0.3px;
    padding-top: 34px;
    padding-bottom: 34px;
  }
`;

const DeskTopTileContent = styled.div`
  ${minTabletOnlyCss};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const QuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-bottom: 15px;
`;

interface Props {
  helpSectionUrl: string;
  helpSectionTitle: string;
  helpSectionIcon?: HelpCenterIconName;
  moreQuestionsLabel: string;
  faqs: Array<{title: string; url: string}>;
}

const HelpSectionTile: FC<Props> = ({
  helpSectionUrl,
  helpSectionIcon,
  helpSectionTitle,
  moreQuestionsLabel,
  faqs,
}) => {
  return (
    <StyledCard data-testid="FaqSectionContainer">
      <HelpSectionLinkMobile href={helpSectionUrl}>
        {helpSectionIcon && (
          <Icon
            data-testid="HelpCenterSectionIcon"
            size={IconSize.M}
            asset={helpSectionIcon}
          />
        )}
        <HelpSectionTitle data-testid="FaqSectionHeadline">
          {helpSectionTitle}
        </HelpSectionTitle>
      </HelpSectionLinkMobile>
      <DeskTopTileContent>
        <TileHeaderLink href={helpSectionUrl}>
          {helpSectionIcon && (
            <Icon
              data-testid="HelpCenterSectionIcon"
              size={IconSize.XL}
              asset={helpSectionIcon}
            />
          )}
          <HelpSectionTitle data-testid="FaqSectionHeadline">
            {helpSectionTitle}
          </HelpSectionTitle>
        </TileHeaderLink>
        <QuestionsContainer>
          {faqs.map(question => (
            <HelpSectionTileQuestionLink
              key={question.url}
              url={question.url}
              label={question.title}
            />
          ))}
        </QuestionsContainer>
        <HelpSectionLinkButton
          data-testid="FaqSectionButton"
          href={helpSectionUrl}
        >
          {moreQuestionsLabel}
        </HelpSectionLinkButton>
      </DeskTopTileContent>
    </StyledCard>
  );
};

export default HelpSectionTile;
